import React, { useState, useEffect } from "react";
import ContinueButton from "../../Registration/ContinueButton";
import Select from "react-select";
import { submitComplianceInfo, setComplianceInfo } from "../../store/supplierFormDataSlice"; 
import { useDispatch, useSelector } from "react-redux";

const RegistrationComplianceForm = ({ onNext }) => {
  const dispatch = useDispatch();
  const registrationAndCompliance = useSelector((state) => state.formData.registrationAndCompliance || {});

  const [formData, setFormData] = useState({
    tax_id: registrationAndCompliance.complianceInfo?.ein?.code || "",
    duns_number: registrationAndCompliance.complianceInfo?.duns?.code || "",
    cage_code: registrationAndCompliance.complianceInfo?.cage?.code || "",
    business_entity_type: registrationAndCompliance.complianceInfo?.entityType || "",
    verification_document: registrationAndCompliance.file || null,
  });
  
  const [errors, setErrors] = useState({});
  const userId = useSelector((state) => state.formData.supplierId);
  

  const isDisabled = !formData.tax_id || !formData.business_entity_type || !formData.verification_document;


  const entityTypes = {
    "Sole Proprietorship": "Business License or DBA Certificate",
    "Limited Liability Company (LLC)":
      "Articles of Organization or Operating Agreement",
    "Corporation (C-Corp / S-Corp / B-Corp)":
      "Articles of Incorporation or Corporate Bylaws",
    "Partnership (General / Limited / LLP)":
      "Partnership Agreement or Certificate of Partnership",
    "Nonprofit Organization":
      "IRS Determination Letter (501(c)(3) or equivalent)",
  };

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      verification_document: e.target.files[0],
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onNext();
    const newErrors = {};
    
    if (!formData.tax_id) newErrors.tax_id = "Tax ID/EIN is required.";
    if (!formData.business_entity_type) newErrors.business_entity_type = "Business entity type is required.";
    if (!formData.verification_document) newErrors.verification_document = "Verification document is required.";
  
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
  
    const complianceInfo = {
      id: userId,
      ein: { name: "Tax ID / EIN", code: formData.tax_id },
      duns: formData.duns_number ? { name: "DUNS Number", code: formData.duns_number } : null,
      cage: formData.cage_code ? { name: "CAGE Code", code: formData.cage_code } : null,
      entityType: formData.business_entity_type,
    };
  
  
const isEmpty = (obj) => !obj || Object.keys(obj).length === 0;

const hasChanges = !isEmpty(registrationAndCompliance) && (
  formData.tax_id !== registrationAndCompliance.complianceInfo?.ein?.code ||
  formData.duns_number !== registrationAndCompliance.complianceInfo?.duns?.code ||
  formData.cage_code !== registrationAndCompliance.complianceInfo?.cage?.code ||
  formData.business_entity_type !== registrationAndCompliance.complianceInfo?.entityType ||
  formData.verification_document !== registrationAndCompliance.file
);

if (isEmpty(registrationAndCompliance) || hasChanges) {
  dispatch(submitComplianceInfo({ file: formData.verification_document, complianceInfo }));
  dispatch(setComplianceInfo({ file: formData.verification_document, complianceInfo }));
}  
    // onNext();
  };
  
  

  const customStyles = {
    menu: (provided) => ({
      ...provided,
      textAlign: "start",
      background: "#F7F7F7",
      borderRadius: "0.5rem",
    }),
    control: (provided, state) => ({
      ...provided,
      border: "none",
      background: "#F9F9F9",
      textAlign: "start",
      height: "48px",
      borderRadius: "0.5rem",
      boxShadow: state.isFocused
        ? "0 0 0 2px rgba(128, 128, 128, 0.5)"
        : provided.boxShadow,
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#1D2639",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "lightgray"
        : state.isFocused
        ? "lightgray"
        : provided.backgroundColor,
      color: "black",
      padding: "6px",
      borderRadius: "0.5rem",
      "&:active": {
        backgroundColor: "lightgray",
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#B0B0B0",
    }),
  };

  return (
    <section className="flex flex-col items-center min-h-screen justify-start bg-white py-10 px-6">
      <header className="text-center mb-14">
        <h1 className="text-2xl font-semibold text-[#1D2639]">
          Registration & Compliance
        </h1>
        <p className="text-base font-medium text-[#7E7E7E] w-full sm:w-[572px] mt-2">
        Upload necessary registration documents and compliance certifications. Demonstrating adherence to industry regulations and standards enhances your credibility and assures buyers of your commitment to lawful operations.
        </p>
      </header>
      <form
        className="flex flex-col w-full sm:w-[572px] gap-4"
        onSubmit={handleSubmit}
        aria-live="polite"
      >
        {/* Tax ID Field */}
        <fieldset className="mb-4">
          <label
            className="block text-[#1D2639] text-[12px] text-start font-normal mb-2"
            htmlFor="tax_id"
          >
            Tax ID / EIN <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="tax_id"
            placeholder="Enter Tax ID / EIN"
            className={`w-full h-12 p-3 bg-[#F9F9F9] rounded-lg  ${
              errors.tax_id ? "border-red-500" : ""
            }`}
            value={formData.tax_id}
            onChange={(e) => handleChange("tax_id", e.target.value)}
          />
          {errors.tax_id && (
            <span
              className="text-red-500 block text-sm text-start mb-2"
              role="alert"
            >
              {errors.tax_id}
            </span>
          )}
        </fieldset>

        {/* DUNS Number Field (Optional) */}
        <fieldset className="mb-4">
          <label
            className="block text-[#1D2639] text-[12px] text-start font-normal mb-2"
            htmlFor="duns_number"
          >
            DUNS Number
          </label>
          <input
            type="text"
            id="duns_number"
            placeholder="Enter DUNS Number (Optional)"
            className={`w-full h-12 p-3 bg-[#F9F9F9] rounded-lg ${
              errors.duns_number ? "border-red-500" : ""
            }`}
            value={formData.duns_number}
            onChange={(e) => handleChange("duns_number", e.target.value)}
          />
          {errors.duns_number && (
            <span
              className="text-red-500 block text-sm text-start mb-2"
              role="alert"
            >
              {errors.duns_number}
            </span>
          )}
        </fieldset>

        {/* CAGE Code Field (Optional) */}
        <fieldset className="mb-4">
          <label
            className="block text-[#1D2639] text-[12px] text-start font-normal mb-2"
            htmlFor="cage_code"
          >
            CAGE Code
          </label>
          <input
            type="text"
            id="cage_code"
            placeholder="Enter CAGE Code (Optional)"
            className={`w-full h-12 p-3 bg-[#F9F9F9] rounded-lg ${
              errors.cage_code ? "border-red-500" : ""
            }`}
            value={formData.cage_code}
            onChange={(e) => handleChange("cage_code", e.target.value)}
          />
          {errors.cage_code && (
            <span
              className="text-red-500 block text-sm text-start mb-2"
              role="alert"
            >
              {errors.cage_code}
            </span>
          )}
        </fieldset>

        {/* Business Entity Type Selection */}
        <fieldset className="mb-4">
          <label
            className="block text-[#1D2639] text-[12px] text-start font-normal mb-2"
            htmlFor="business_entity_type"
          >
            Select Business Entity Type <span className="text-red-500">*</span>
          </label>
              <Select
  id="business_entity_type"
  name="business_entity_type"
  options={Object.keys(entityTypes).map((type) => ({
    label: type,
    value: type,
  }))}
  value={
    formData.business_entity_type
      ? { label: formData.business_entity_type, value: formData.business_entity_type }
      : null
  }
  onChange={(selectedOption) =>
    handleChange("business_entity_type", selectedOption?.value)
  }
  styles={customStyles}
  placeholder="Select Business Entity"
/>

          {errors.business_entity_type && (
            <span
              className="text-red-500 block text-sm text-start mb-2"
              role="alert"
            >
              {errors.business_entity_type}
            </span>
          )}
        </fieldset>

        {/* Document Upload */}
        {formData.business_entity_type && (
          <fieldset className="mb-4">
            <label
              className="block text-[#1D2639] text-[12px] text-start font-normal mb-2"
              htmlFor="verification_document"
            >
              Upload Verification Document{" "}
              <span className="text-red-500">*</span>
            </label>
            <p className="text-sm text-gray-600 mt-2 text-start">
              Required: {entityTypes[formData.business_entity_type]}
            </p>
            <input
              type="file"
              id="verification_document"
              className="w-full h-12 p-3 bg-[#F9F9F9] rounded-lg text-gray-500 "
              onChange={handleFileChange}
            />
            {errors.verification_document && (
              <span
                className="text-red-500 block text-sm text-start mb-2"
                role="alert"
              >
                {errors.verification_document}
              </span>
            )}
          </fieldset>
        )}

        <ContinueButton
          isDisabled={isDisabled}
          onClick={handleSubmit }
          label="Save and Continue"
        />
      </form>
    </section>
  );
};

export default RegistrationComplianceForm;
